import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Handshake, Plus, Copy, CheckCircle, Gear, Receipt as ReceiptIcon, FileText, Trash, Gavel, Certificate as Award, ChartLine, PaperPlaneTilt, Clock, PencilSimple } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  getAdhesionSettings, updateAdhesionSettings, sendAdhesionKit,
  listAdhesionKits, markAdhesionPaidBank, downloadAdhesionReceipt,
  getAdhesionKpis, uploadKitAttachments, deleteKitAttachment,
  getAdhesionCapitalMonthly, getKitApplication, postKitDecision, getKitCertificate,
  downloadRegistry, getAdhesionKanban, runPaymentReminders,
  patchKitStatus, getRemindersStats, sendManualReminder, bulkPatchKitsStatus, toggleKitReminders,
  getContacts, getOrganizations,
  updateAdhesionKit,
  listOfficialAdhesionKits,
} from '../services/api';
import { Capital3DBars } from '../components/motion/Capital3DBars';
import { PageHero } from '../components/motion/PageHero';
import { KitPdfActions } from '../components/adhesion/KitPdfActions';
import { KitEmailActions, KitEmailStatus } from '../components/adhesion/KitEmailActions';
import { OfficialKitLibrary } from '../components/adhesion/OfficialKitLibrary';
import { useAuth } from '../contexts/AuthContext';
import { DigitalInstructionDialog } from '../components/adhesion/digital/DigitalInstructionDialog';
import { DossierReference } from '../components/adhesion/DossierReference';

const KIT_TYPES = [
  { value: 'associe_pp', label: 'Associé coopérateur — Personne physique' },
  { value: 'associe_pm', label: 'Associé coopérateur — Personne morale' },
  { value: 'partenaire', label: 'Partenaire O\'SCOP' },
];

const STATUS_COLORS = {
  draft: 'bg-red-100 text-red-800',
  sent: 'bg-blue-100 text-blue-800',
  pending_transfer: 'bg-amber-100 text-amber-800',
  paid: 'bg-emerald-100 text-emerald-800',
  expired: 'bg-gray-200 text-gray-700',
  cancelled: 'bg-red-100 text-red-800',
};
const STATUS_LABELS = { draft: 'À envoyer', sent: 'Envoyé', submitted: 'Soumis', under_review: 'En instruction', approved: 'Agréé', adjourned: 'Ajourné', refused: 'Refusé', pending_transfer: 'Virement attendu', paid: 'Payé', expired: 'Expiré', cancelled: 'Annulé' };

// Calcule le nombre de jours écoulés dans le statut courant d'un kit (miroir client de _pending_since côté backend)
function computePendingDays(k) {
  if (!k || k.status === 'paid') return null;
  const iso =
    ((k.status === 'approved' || k.status === 'adjourned' || k.status === 'refused') && k.decision?.decided_at) ||
    (k.status === 'submitted' && k.submitted_at) ||
    k.status_changed_at || k.updated_at || k.created_at;
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24)));
}

function PendingBadge({ days, testId }) {
  if (days == null) return null;
  const tone = days > 14 ? 'bg-red-100 text-red-800' : days >= 7 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${tone}`}
      data-testid={testId}
    >
      <Clock size={10} weight="bold" /> En attente depuis {days === 0 ? "aujourd'hui" : `${days} j`}
    </span>
  );
}

export default function AdhesionPage() {
  const { user } = useAuth();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [openSend, setOpenSend] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [openAttach, setOpenAttach] = useState(null);
  const [openInstruction, setOpenInstruction] = useState(null);
  const [chart, setChart] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'kanban'
  const [kanban, setKanban] = useState(null);
  const [remindersStats, setRemindersStats] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'associe_pp' | 'associe_pm' | 'partenaire'
  const [openTimeline, setOpenTimeline] = useState(null);
  const [openEdit, setOpenEdit] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const toggleSelect = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x)=>x!==id) : [...prev, id]);
  const clearSelection = () => setSelectedIds([]);
  const bulkMove = async (targetStatus) => {
    if (selectedIds.length === 0) return;
    try {
      const r = await bulkPatchKitsStatus(selectedIds, targetStatus);
      toast.success(`${r.data.updated} kit(s) déplacé(s)${r.data.skipped_paid ? ` (${r.data.skipped_paid} finalisé(s) ignoré(s))` : ''}`);
      clearSelection();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [k, s, kp, cm, kb, rs] = await Promise.all([listAdhesionKits({ limit: 200 }), getAdhesionSettings(), getAdhesionKpis(), getAdhesionCapitalMonthly(), getAdhesionKanban(), getRemindersStats()]);
      setKits(k.data.items || []);
      setSettings(s.data);
      setKpis(kp.data);
      setChart(cm.data.items || []);
      setKanban(kb.data);
      setRemindersStats(rs.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleMarkPaid = async (id) => {
    if (!window.confirm('Confirmer la réception du virement ? Ceci créera automatiquement le compte portail.')) return;
    try { await markAdhesionPaidBank(id); toast.success('Kit finalisé, portail créé'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const handleReceipt = async (id) => {
    try {
      const r = await downloadAdhesionReceipt(id);
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch { toast.error('Reçu indisponible'); }
  };

  return (
    <div className="p-2 sm:p-4 xl:p-6 space-y-4 min-w-0" data-testid="adhesion-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Kits d'adhésion"
        subtitle="Candidatures, parts sociales et suivi Kanban de bout en bout"
        icon={<Handshake size={24} weight="duotone" className="text-white" />}
        gradient="from-[#FF8E00] to-[#EA3C3A]"
        accents={["#80C51F", "#00AEEF"]}
      />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0 flex-1 basis-80">
          <h1 className="text-2xl font-semibold text-[#1A2421] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Handshake size={22} className="text-[#2D5A4C]" /> Kits d'adhésion
          </h1>
          <p className="text-sm text-[#5E6D66]">Envoyez le pack d'adhésion complet, encaissez la part sociale, l'espace coopérateur est créé automatiquement.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild><Link to="/adhesion/registre" data-testid="open-associate-registry"><FileText size={16} className="mr-1" />Registre des associés</Link></Button>
          <Button variant="outline" onClick={async () => {
            const r = await downloadRegistry();
            const cd = r.headers?.['content-disposition'] || '';
            const match = cd.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `registre-associes-${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
          }} data-testid="download-registry-btn"><FileText size={16} className="mr-1" /> Registre PDF</Button>
          <Button variant="outline" onClick={async () => {
            const r = await runPaymentReminders();
            toast.success(`Relances envoyées : ${r.data.reminded}/${r.data.candidates}`);
          }} data-testid="run-reminders-btn">Rappel J+15</Button>
          <Button variant="outline" onClick={() => setView(view === 'list' ? 'kanban' : 'list')} data-testid="toggle-view">
            {view === 'list' ? 'Vue Kanban' : 'Vue Liste'}
          </Button>
          <Button variant="outline" onClick={() => setOpenSettings(true)} data-testid="adhesion-settings-btn"><Gear size={16} className="mr-1" /> Paramètres</Button>
          <Button onClick={() => setOpenSend(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="new-kit-btn"><Plus size={16} className="mr-1" /> Envoyer un kit</Button>
        </div>
      </div>

      {user?.role === 'super_admin' && <OfficialKitLibrary />}

      {kpis && (
        <div className="grid grid-cols-1 min-[400px]:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="adhesion-kpi-strip">
          <Card className="border-[#E8E6E1] motion-fade-up motion-fade-up-delay-0 motion-shine"><CardContent className="p-4">
            <p className="text-[10px] uppercase text-[#5E6D66]">Kits envoyés</p>
            <p className="text-3xl font-bold text-blue-700" data-testid="kpi-sent">{kpis.sent}</p>
          </CardContent></Card>
          <Card className="border-[#E8E6E1] motion-fade-up motion-fade-up-delay-1 motion-shine"><CardContent className="p-4">
            <p className="text-[10px] uppercase text-[#5E6D66]">Virements attendus</p>
            <p className="text-3xl font-bold text-amber-700" data-testid="kpi-pending">{kpis.pending_transfer}</p>
          </CardContent></Card>
          <Card className="border-[#E8E6E1] motion-fade-up motion-fade-up-delay-2 motion-shine"><CardContent className="p-4">
            <p className="text-[10px] uppercase text-[#5E6D66]">Payés ce mois</p>
            <p className="text-3xl font-bold text-emerald-700" data-testid="kpi-paid-month">{kpis.paid_this_month}</p>
          </CardContent></Card>
          <Card className="border-[#E8E6E1] motion-fade-up motion-fade-up-delay-3 motion-shine"><CardContent className="p-4">
            <p className="text-[10px] uppercase text-[#5E6D66]">Capital levé</p>
            <p className="text-2xl font-bold text-[#2D5A4C]" data-testid="kpi-capital">
              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(kpis.total_capital_raised)}
            </p>
          </CardContent></Card>
        </div>
      )}

      {chart.length > 0 && (
        <Card className="border-[#E8E6E1] motion-fade-up motion-fade-up-delay-4" data-testid="capital-chart">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#5E6D66] flex items-center gap-1"><ChartLine size={12}/> Capital levé — évolution mensuelle (vue 3D immersive)</p>
            <Capital3DBars data={chart} height={200} testId="adhesion-capital-3d" />
          </CardContent>
        </Card>
      )}

      {loading && <Loader2 className="animate-spin" />}
      {!loading && kits.length === 0 && (
        <Card className="border-[#E8E6E1]"><CardContent className="p-8 text-center text-[#5E6D66]">Aucun kit envoyé pour l'instant.</CardContent></Card>
      )}

      {view === 'kanban' && kanban && (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-5 gap-3 motion-fade-up motion-fade-up-delay-5" data-testid="adhesion-kanban">
          <div className="col-span-full flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs text-[#5E6D66]">Filtrer par type :</span>
            {[
              { v: 'all', l: 'Tous' },
              { v: 'associe_pp', l: 'PP (personnes physiques)' },
              { v: 'associe_pm', l: 'PM (personnes morales)' },
              { v: 'partenaire', l: 'Partenaires' },
            ].map((f) => (
              <Button key={f.v} size="sm" variant={typeFilter === f.v ? 'default' : 'outline'}
                className={typeFilter === f.v ? 'bg-[#2D5A4C] hover:bg-[#1E4136] h-7 text-xs' : 'h-7 text-xs'}
                onClick={() => setTypeFilter(f.v)} data-testid={`filter-${f.v}`}>
                {f.l}
              </Button>
            ))}
            {selectedIds.length > 0 && (
              <div className="ml-auto flex items-center gap-1 bg-[#2D5A4C] text-white px-2 py-1 rounded text-xs" data-testid="bulk-action-bar">
                <span className="mr-2"><strong>{selectedIds.length}</strong> sélectionné(s)</span>
                {[{k:'sent',l:'→ Envoyé'},{k:'submitted',l:'→ Soumis'},{k:'approved',l:'→ Agréé'}].map((a) => (
                  <button key={a.k} onClick={() => bulkMove(a.k)} className="hover:bg-[#1E4136] px-2 py-0.5 rounded" data-testid={`bulk-move-${a.k}`}>{a.l}</button>
                ))}
                <button onClick={clearSelection} className="ml-2 hover:bg-[#1E4136] px-2 rounded" data-testid="bulk-clear">×</button>
              </div>
            )}
          </div>
          {[
            { key: 'draft', label: 'À envoyer', color: 'bg-red-50 border-red-200' },
            { key: 'sent', label: 'Envoyé', color: 'bg-blue-50 border-blue-200' },
            { key: 'submitted', label: 'Soumis', color: 'bg-amber-50 border-amber-200' },
            { key: 'approved', label: 'Agréé (en attente paiement)', color: 'bg-purple-50 border-purple-200' },
            { key: 'paid', label: 'Associé', color: 'bg-emerald-50 border-emerald-200' },
          ].filter((col) => {
            if (typeFilter === 'all') return true;
            const filtered = (kanban.columns[col.key] || []).filter((c) => c.kit_type === typeFilter);
            return filtered.length > 0;
          }).map((col) => {
            const cards = (kanban.columns[col.key] || []).filter((c) => typeFilter === 'all' || c.kit_type === typeFilter);
            return (
            <div
              key={col.key}
              className={`p-2 rounded border ${col.color}`}
              data-testid={`kanban-col-${col.key}`}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-[#2D5A4C]'); }}
              onDragLeave={(e) => e.currentTarget.classList.remove('ring-2', 'ring-[#2D5A4C]')}
              onDrop={async (e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('ring-2', 'ring-[#2D5A4C]');
                const kitId = e.dataTransfer.getData('kit-id');
                const from = e.dataTransfer.getData('kit-status');
                if (!kitId || from === col.key || col.key === 'draft') return;
                try {
                  await patchKitStatus(kitId, col.key);
                  toast.success(`Kit déplacé vers « ${col.label} »`);
                  load();
                } catch (err) { toast.error(err?.response?.data?.detail || 'Déplacement refusé'); }
              }}
            >
              <p className="text-xs font-semibold uppercase mb-2 flex items-center justify-between">
                <span>{col.label}</span>
                <span className="bg-white px-2 py-0.5 rounded text-[10px]">{cards.length}</span>
              </p>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {cards.map((c) => (
                  <div
                    key={c.id}
                    className={`p-2 bg-white rounded text-[11px] border ${selectedIds.includes(c.id) ? 'border-[#2D5A4C] ring-2 ring-[#2D5A4C]' : 'border-[#E8E6E1]'} cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow`}
                    data-testid={`kanban-card-${c.id}`}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('kit-id', c.id);
                      e.dataTransfer.setData('kit-status', c.status);
                      e.currentTarget.classList.add('opacity-50');
                    }}
                    onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                  >
                    <div className="flex items-start gap-1">
                      <input type="checkbox" checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} className="mt-0.5" data-testid={`kanban-select-${c.id}`} onClick={(e) => e.stopPropagation()} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{c.beneficiary}</p>
                        <DossierReference reference={c.dossier_reference} testId={`kanban-dossier-reference-${c.id}`} />
                        <p className="text-[#5E6D66] truncate">{c.email || '—'}</p>
                        <p className="mt-1">{c.kit_type === 'associe_pp' ? '👤 PP' : c.kit_type === 'associe_pm' ? '🏢 PM' : '🤝 Partenaire'} · {c.amount ? `${c.amount} €` : '—'}</p>
                        {c.associate_number && <p className="mt-1 font-mono text-emerald-700">{c.associate_number}</p>}
                        {c.reminder_count >= 2 && (
                          <div className="mt-1 inline-flex items-center gap-1 bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-[10px] font-semibold" data-testid={`reminder-alert-${c.id}`}>
                            ⚠ {c.reminder_count} relances
                          </div>
                        )}
                        {c.reminders_paused && (
                          <div className="mt-1 inline-flex items-center gap-1 bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px] font-semibold" data-testid={`reminders-paused-${c.id}`}>
                            ⏸ Relances en pause
                          </div>
                        )}
                        {c.status !== 'paid' && c.pending_days != null && (
                          <div
                            className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              c.pending_days > 14 ? 'bg-red-100 text-red-800' : c.pending_days >= 7 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                            }`}
                            data-testid={`pending-badge-${c.id}`}
                            title={`Dans le statut « ${col.label} » depuis ${c.pending_since ? new Date(c.pending_since).toLocaleDateString('fr-FR') : '—'}`}
                          >
                            <Clock size={10} weight="bold" /> En attente depuis {c.pending_days === 0 ? "aujourd'hui" : `${c.pending_days} j`}
                          </div>
                        )}
                        {(c.status === 'approved' || c.status === 'pending_transfer') && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const r = await toggleKitReminders(c.id);
                                toast.success(r.data.reminders_paused ? 'Relances mises en pause' : 'Relances réactivées');
                                load();
                              } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
                            }}
                            className="mt-1 text-[10px] text-[#2D5A4C] underline hover:no-underline"
                            data-testid={`toggle-reminders-${c.id}`}
                          >
                            {c.reminders_paused ? 'Réactiver relances' : 'Annuler relances'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {remindersStats && (
        <Card className="border-[#E8E6E1]" data-testid="reminders-history">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase text-[#5E6D66] font-semibold">Relances J+15 — Historique</p>
              <div className="flex gap-4 text-xs">
                <span data-testid="reminders-this-month"><strong>{remindersStats.this_month}</strong> ce mois</span>
                <span className="text-[#5E6D66]"><strong>{remindersStats.total}</strong> au total</span>
              </div>
            </div>
            {remindersStats.recent.length === 0 ? (
              <p className="text-[11px] text-[#5E6D66] py-2">Aucune relance envoyée.</p>
            ) : (
              <div className="max-h-40 overflow-y-auto text-[11px]">
                {remindersStats.recent.slice(0, 10).map((r) => (
                  <div key={r.id} className="flex items-center justify-between border-t border-[#E8E6E1] py-1" data-testid={`reminder-row-${r.id}`}>
                    <div className="flex-1 truncate">
                      <span>{r.application?.identity?.nom || r.beneficiary?.name || '—'}</span>
                      <span className="text-[#5E6D66]"> · {r.application?.identity?.email || r.beneficiary?.email}</span>
                    </div>
                    <span className="text-[#5E6D66] mx-2">{(r.payment_reminded_at || '').slice(0, 10)}</span>
                    <Button size="sm" variant="ghost" onClick={async () => {
                      try { await sendManualReminder(r.id); toast.success('Relance envoyée'); load(); }
                      catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
                    }} data-testid={`resend-${r.id}`} title="Renvoyer la relance">
                      <PaperPlaneTilt size={12} className="text-[#2D5A4C]"/>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {view === 'list' && (
      <div className="grid gap-2 motion-fade-up motion-fade-up-delay-5">
        {kits.map((k) => (
          <Card key={k.id} className="border-[#E8E6E1] motion-shine" data-testid={`kit-card-${k.id}`}>
            <CardContent className="p-3 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0 basis-64">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#1A2421] truncate" data-testid={`kit-beneficiary-${k.id}`}>{k.beneficiary?.name}</span>
                  <Badge className={`${STATUS_COLORS[k.status]} text-[10px]`} data-testid={`kit-status-${k.id}`}>{STATUS_LABELS[k.status] || k.status}</Badge>
                  <Badge variant="outline" className="text-[10px] max-w-full whitespace-normal break-words" data-testid={`kit-type-label-${k.id}`}>{KIT_TYPES.find((t) => t.value === k.kit_type)?.label}</Badge>
                  {k.receipt_number && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">{k.receipt_number}</Badge>}
                  <PendingBadge days={computePendingDays(k)} testId={`list-pending-badge-${k.id}`} />
                  {k.reminders_paused && (
                    <span className="inline-flex items-center gap-1 bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px] font-semibold" data-testid={`list-reminders-paused-${k.id}`}>
                      ⏸ Relances en pause
                    </span>
                  )}
                </div>
                <p className="text-xs text-[#5E6D66] break-words" data-testid={`kit-details-${k.id}`}>{k.beneficiary?.email} · {Number(k.amount || 0).toFixed(2)} {k.currency?.toUpperCase()} · Créé le {(k.created_at || '').slice(0, 10)}</p>
                <DossierReference reference={k.dossier_reference} testId={`kit-dossier-reference-${k.id}`} administrative={!!k.digital?.member_signature && !k.dossier_reference_in_signed_content} />
                <KitEmailStatus kit={k} />
                <p className="text-xs text-[#5E6D66] mt-1 break-words" data-testid={`kit-document-version-${k.id}`}>{k.official_template ? `PDF officiel · version ${k.official_template.version} · ${k.official_template.filename}` : 'Modèle généré historique'}</p>
              </div>
              <div className="flex flex-wrap items-center gap-1 max-w-full">
                {k.status === 'pending_transfer' && (
                  <Button size="sm" variant="ghost" onClick={() => handleMarkPaid(k.id)} data-testid={`mark-paid-${k.id}`}>
                    <CheckCircle size={14} className="text-emerald-600" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => {
                  const url = `${window.location.origin}/adhesion/${k.token}`;
                  navigator.clipboard.writeText(url);
                  toast.success('Lien copié — partagez par SMS/WhatsApp/email');
                }} data-testid={`copy-link-kit-${k.id}`} title="Copier le lien du kit">
                  <Copy size={14} />
                </Button>
                <KitPdfActions kitId={k.id} token={k.token} />
                <KitEmailActions kit={k} onChanged={load} />
                {k.status !== 'paid' && !k.digital_form_key && (
                  <Button size="sm" variant="ghost" onClick={() => setOpenEdit(k)} data-testid={`edit-kit-${k.id}`} title="Modifier le kit">
                    <PencilSimple size={14} className="text-blue-700" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setOpenTimeline(k)} data-testid={`timeline-${k.id}`} title="Timeline du dossier">
                  <Clock size={14} className="text-blue-700" />
                </Button>
                {k.digital_form_key || k.status === 'submitted' || k.status === 'under_review' ? (
                  <Button size="sm" variant="ghost" onClick={() => setOpenInstruction(k)} data-testid={`instruct-${k.id}`} title="Instruire le dossier">
                    <Gavel size={14} className="text-amber-700" />
                  </Button>
                ) : null}
                {k.status === 'paid' && k.associate_number && (
                  <Button size="sm" variant="ghost" onClick={async () => {
                    const r = await getKitCertificate(k.id);
                    const url = URL.createObjectURL(r.data);
                    window.open(url, '_blank');
                  }} data-testid={`certificate-${k.id}`} title="Télécharger le certificat">
                    <Award size={14} className="text-emerald-700" />
                  </Button>
                )}
                {k.receipt_number && (
                  <Button size="sm" variant="ghost" onClick={() => handleReceipt(k.id)} data-testid={`receipt-${k.id}`}>
                    <ReceiptIcon size={14} className="text-[#2D5A4C]" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setOpenAttach(k)} data-testid={`attach-${k.id}`} title="Documents joints">
                  <FileText size={14} />
                  {(k.attachments?.length || 0) > 0 && <span className="ml-1 text-[10px]">{k.attachments.length}</span>}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      {openSend && <SendKitDialog onClose={() => setOpenSend(false)} onSent={() => { setOpenSend(false); load(); }} />}
      {openSettings && <SettingsDialog settings={settings} onClose={() => setOpenSettings(false)} onSaved={() => { setOpenSettings(false); load(); }} />}
      {openAttach && <AttachmentsDialog kit={openAttach} onClose={() => setOpenAttach(null)} onChanged={() => { load(); setOpenAttach(null); }} />}
      {openInstruction && (openInstruction.digital_form_key ? <DigitalInstructionDialog kit={openInstruction} onClose={() => { load(); setOpenInstruction(null); }} onDone={() => { load(); setOpenInstruction(null); }} /> : <InstructionDialog kit={openInstruction} onClose={() => setOpenInstruction(null)} onDone={() => { load(); setOpenInstruction(null); }} />)}
      {openTimeline && <TimelineDialog kit={openTimeline} onClose={() => setOpenTimeline(null)} />}
      {openEdit && <EditKitDialog kit={openEdit} onClose={() => setOpenEdit(null)} onSaved={() => { setOpenEdit(null); load(); }} />}
    </div>
  );
}

function TimelineDialog({ kit, onClose }) {
  const events = [
    { key: 'created_at', label: 'Kit envoyé', date: kit.created_at, actor: kit.created_by || 'Système' },
    { key: 'submitted_at', label: 'Dossier soumis par le candidat', date: kit.submitted_at, actor: kit.beneficiary?.email || kit.application?.identity?.email || '—' },
    { key: 'decided_at', label: `Décision d'instruction (${kit.decision?.outcome === 'approved' ? 'agrément' : kit.decision?.outcome || '—'})`, date: kit.decision?.decided_at, actor: kit.decision?.decided_by },
    { key: 'payment_reminded_at', label: 'Relance de paiement J+15', date: kit.payment_reminded_at, actor: 'CRM (cron)' },
    { key: 'paid_at', label: `Part sociale libérée (${kit.payment_method || '—'})`, date: kit.paid_at, actor: kit.finalized_by || 'webhook Stripe' },
  ].filter((e) => e.date);
  const fmt = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="timeline-dialog">
        <DialogHeader>
          <DialogTitle>Timeline — {kit.application?.identity?.nom || kit.beneficiary?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-0 max-h-[60vh] overflow-y-auto">
          {events.length === 0 && <p className="text-xs text-[#5E6D66] py-4 text-center">Aucun événement enregistré.</p>}
          {events.map((e, i) => (
            <div key={i} className="flex gap-3 relative" data-testid={`timeline-event-${e.key}`}>
              <div className="flex flex-col items-center">
                <div className={`w-3 h-3 rounded-full border-2 ${i === events.length - 1 ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-[#2D5A4C]'}`} />
                {i < events.length - 1 && <div className="w-0.5 flex-1 bg-[#E8E6E1] min-h-[24px]" />}
              </div>
              <div className="pb-4 flex-1">
                <p className="text-xs font-semibold text-[#1A2421]">{e.label}</p>
                <p className="text-[11px] text-[#5E6D66]">{fmt(e.date)}</p>
                {e.actor && <p className="text-[10px] text-[#5E6D66] italic">par {e.actor}</p>}
              </div>
            </div>
          ))}
        </div>
        {kit.associate_number && (
          <div className="bg-emerald-50 border border-emerald-200 p-2 rounded text-xs">
            <strong>N° d'associé :</strong> {kit.associate_number}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstructionDialog({ kit, onClose, onDone }) {
  const [detail, setDetail] = useState(null);
  const [outcome, setOutcome] = useState('approved');
  const [college, setCollege] = useState('G');
  const [collegeRetenu, setCollegeRetenu] = useState('');
  const [avis, setAvis] = useState('');
  const [conditions, setConditions] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { getKitApplication(kit.id).then((r) => {
    setDetail(r.data);
    // Pour PP : collège auto G ; pour PM/partenaire : reprendre le collège proposé
    const kt = r.data?.kit_type;
    const proposed = r.data?.application?.contribution?.college;
    if (kt === 'associe_pp') { setCollege('G'); setCollegeRetenu('G'); }
    else if (proposed) { setCollege(proposed); setCollegeRetenu(proposed); }
  }).catch(() => toast.error('Erreur chargement')); }, [kit.id]);
  const submit = async () => {
    setSaving(true);
    try {
      await postKitDecision(kit.id, { outcome, college_affecte: college, college_retenu: collegeRetenu, avis, conditions });
      toast.success(outcome === 'approved' ? 'Agrément prononcé — email envoyé au candidat' : 'Décision enregistrée');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };
  const app = detail?.application || {};
  const isPP = detail?.kit_type === 'associe_pp';
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="instruction-dialog">
        <DialogHeader><DialogTitle>Instruire le dossier de candidature</DialogTitle></DialogHeader>
        {!detail ? <Loader2 className="animate-spin"/> : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            <div className="bg-[#F5F4EF] p-3 rounded text-xs space-y-1">
              <p><strong>{app.identity?.nom} {app.identity?.prenoms}</strong> — {app.identity?.email}</p>
              <p>Type : {detail.kit_type_label} · Situation : {app.identity?.situation} · Métier : {app.contribution?.metier}</p>
              <p>Description : {app.contribution?.description?.slice(0, 200) || '—'}</p>
              <p>Parts demandées : {app.subscription?.nombre_parts || 1} × 375 € = {(app.subscription?.nombre_parts || 1) * 375} €</p>
              <p>Pièces : {Object.keys(detail.pieces || {}).length}/9 téléversées</p>
              {!isPP && <p>Collège proposé par le demandeur : <strong>{app.contribution?.college || '—'}</strong></p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Décision</Label>
                <Select value={outcome} onValueChange={setOutcome}>
                  <SelectTrigger data-testid="decision-outcome"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Agrément</SelectItem>
                    <SelectItem value="adjourned">Ajournement</SelectItem>
                    <SelectItem value="refused">Refus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Collège retenu après instruction</Label>
                {isPP ? (
                  <Input value="G — Coopérateurs contributeurs (auto)" disabled data-testid="college-retenu-pp"/>
                ) : (
                  <Select value={collegeRetenu} onValueChange={(v)=>{setCollegeRetenu(v); setCollege(v);}}>
                    <SelectTrigger data-testid="college-retenu"><SelectValue placeholder="—"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="G">G — Coopérateurs contributeurs</SelectItem>
                      <SelectItem value="C">C — Partenaires, collectivités et organismes d'appui</SelectItem>
                      <SelectItem value="D">D — Fédérations, organisations professionnelles</SelectItem>
                      <SelectItem value="F">F — Utilisateurs, bénéficiaires et habitants</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div><Label>Avis motivé</Label><Textarea rows={2} value={avis} onChange={(e)=>setAvis(e.target.value)}/></div>
            <div><Label>Conditions éventuelles</Label><Textarea rows={2} value={conditions} onChange={(e)=>setConditions(e.target.value)}/></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={saving || !detail} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="decision-submit">
            {saving && <Loader2 size={14} className="animate-spin mr-2"/>} Enregistrer la décision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentsDialog({ kit, onClose, onChanged }) {
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const attachments = kit.attachments || [];

  const handleUpload = async () => {
    if (!files || files.length === 0) return;
    setSaving(true);
    try {
      await uploadKitAttachments(kit.id, files);
      toast.success('Documents joints ajoutés');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (attId) => {
    try { await deleteKitAttachment(kit.id, attId); toast.success('Supprimé'); onChanged(); }
    catch { toast.error('Erreur'); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="attachments-dialog">
        <DialogHeader><DialogTitle>Documents joints au kit</DialogTitle></DialogHeader>
        <p className="text-xs text-[#5E6D66]">Ces PDFs seront concaténés à la fin du kit d'adhésion officiel lors du téléchargement par le bénéficiaire.</p>
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {attachments.length === 0 && <p className="text-xs text-[#5E6D66] text-center py-3">Aucun document joint</p>}
          {attachments.map((a) => (
            <div key={a.id} className="flex items-center gap-2 p-2 border border-[#E8E6E1] rounded text-xs" data-testid={`attach-item-${a.id}`}>
              <FileText size={14} />
              <span className="flex-1 truncate">{a.filename}</span>
              <span className="text-[10px] text-[#5E6D66]">{Math.round(a.size / 1024)} Ko</span>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(a.id)} data-testid={`attach-delete-${a.id}`}>
                <Trash size={12} className="text-red-600" />
              </Button>
            </div>
          ))}
        </div>
        <div className="border-t border-[#E8E6E1] pt-3 space-y-2">
          <Label className="text-xs">Ajouter des PDFs (max 15 Mo chacun)</Label>
          <input type="file" accept="application/pdf" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="attach-file-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button onClick={handleUpload} disabled={saving || files.length === 0} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="attach-upload-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Téléverser
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendKitDialog({ onClose, onSent }) {
  const [kitType, setKitType] = useState('associe_pp');
  const [contactId, setContactId] = useState('');
  const [orgId, setOrgId] = useState('');
  const [amount, setAmount] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [contacts, setContacts] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [officialProfiles, setOfficialProfiles] = useState(null);
  const [officialError, setOfficialError] = useState('');

  useEffect(() => {
    listOfficialAdhesionKits().then((r) => setOfficialProfiles(r.data.items)).catch(() => setOfficialError('Impossible de vérifier les kits officiels en vigueur.'));
    (async () => {
      try {
        const r = await getContacts({ limit: 200 });
        setContacts((r.data.items || []).filter((c) => c.email));
      } catch {}
      try {
        const r = await getOrganizations({ limit: 200 });
        setOrgs((r.data.items || []).filter((o) => o.email));
      } catch {}
    })();
  }, []);

  const isPM = kitType === 'associe_pm';
  const currentOfficial = officialProfiles?.find((p) => p.kit_type === kitType)?.current;
  const needsOfficial = kitType !== 'partenaire';
  const blockedOfficial = needsOfficial && !currentOfficial;

  const handleSend = async () => {
    if (isPM && !orgId) { toast.error('Sélectionnez une organisation'); return; }
    if (!isPM && !contactId) { toast.error('Sélectionnez un contact'); return; }
    setSaving(true);
    try {
      await sendAdhesionKit({
        kit_type: kitType,
        contact_id: !isPM ? contactId : undefined,
        organization_id: isPM ? orgId : undefined,
        amount: amount ? Number(amount) : undefined,
        signer_email: signerEmail.trim() || undefined,
      });
      toast.success('Kit PDF transmis au service email');
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
      if (e?.response?.status === 502) onSent();
    }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="send-kit-dialog">
        <DialogHeader><DialogTitle>Envoyer un kit d'adhésion</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={kitType} onValueChange={(value) => { setKitType(value); setContactId(''); setOrgId(''); setSignerEmail(''); }}>
              <SelectTrigger data-testid="kit-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KIT_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`kit-type-${t.value}`}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {needsOfficial && <p className={`text-xs break-words ${currentOfficial ? 'text-[#2D5A4C]' : 'text-amber-800'}`} data-testid="send-kit-official-version">
            {officialError || (officialProfiles === null ? 'Vérification du PDF officiel…' : currentOfficial ? `PDF officiel · version ${currentOfficial.version} · ${currentOfficial.filename}` : 'Aucun kit officiel pour ce profil. Import requis par le super admin avant envoi.')}
          </p>}
          {!isPM ? (
            <div>
              <Label className="text-xs">Contact bénéficiaire (avec email)</Label>
              <Select value={contactId} onValueChange={setContactId}>
                <SelectTrigger data-testid="kit-contact-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {contacts.slice(0, 100).map((c) => <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom} · {c.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Organisation</Label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger data-testid="kit-org-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {orgs.slice(0, 100).map((o) => <SelectItem key={o.id} value={o.id}>{o.raison_sociale} · {o.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            {currentOfficial?.form_key && <div className="mb-3"><Label htmlFor="kit-signer-email">Email du signataire {isPM ? '(représentant légal)' : '(adhérent)'}</Label><Input id="kit-signer-email" type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder="À défaut : email du bénéficiaire sélectionné" data-testid="kit-signer-email" /></div>}
            <Label className="text-xs">{currentOfficial?.form_key ? 'Montant prévu : multiple de 375 € (375 € par défaut)' : 'Montant (laisser vide = valeur configurée)'}</Label>
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="kit-amount" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kit-send-cancel">Annuler</Button>
          <Button onClick={handleSend} disabled={saving || blockedOfficial} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="kit-send-submit">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ settings, onClose, onSaved }) {
  const [pp, setPP] = useState(settings?.amount_pp || 375);
  const [pm, setPM] = useState(settings?.amount_pm || 375);
  const [part, setPart] = useState(settings?.amount_partenaire || 375);
  const [bank, setBank] = useState(settings?.bank || {});
  const [ribFile, setRibFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAdhesionSettings({ amount_pp: Number(pp), amount_pm: Number(pm), amount_partenaire: Number(part), bank });
      if (ribFile) {
        const { uploadRibPdf } = await import('../services/api');
        await uploadRibPdf(ribFile);
      }
      toast.success('Paramètres enregistrés');
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="adhesion-settings-dialog">
        <DialogHeader><DialogTitle>Paramètres d'adhésion</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs uppercase text-[#5E6D66] font-semibold pt-2">Montants de part sociale</p>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">PP (€)</Label><Input type="number" value={pp} onChange={(e) => setPP(e.target.value)} data-testid="settings-pp" /></div>
            <div><Label className="text-xs">PM (€)</Label><Input type="number" value={pm} onChange={(e) => setPM(e.target.value)} data-testid="settings-pm" /></div>
            <div><Label className="text-xs">Partenaire (€)</Label><Input type="number" value={part} onChange={(e) => setPart(e.target.value)} data-testid="settings-partenaire" /></div>
          </div>
          <p className="text-xs uppercase text-[#5E6D66] font-semibold pt-4">RIB officiel — virement de part sociale</p>
          <div className="grid grid-cols-1 gap-2">
            <div><Label className="text-xs">Titulaire</Label><Input value={bank.titulaire || ''} onChange={(e) => setBank({ ...bank, titulaire: e.target.value })} data-testid="settings-titulaire" /></div>
            <div><Label className="text-xs">IBAN</Label><Input value={bank.iban || ''} onChange={(e) => setBank({ ...bank, iban: e.target.value })} data-testid="settings-iban" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">BIC</Label><Input value={bank.bic || ''} onChange={(e) => setBank({ ...bank, bic: e.target.value })} data-testid="settings-bic" /></div>
              <div><Label className="text-xs">BIC partenaire</Label><Input value={bank.bic_partenaire || ''} onChange={(e) => setBank({ ...bank, bic_partenaire: e.target.value })} /></div>
            </div>
            <div><Label className="text-xs">Banque</Label><Input value={bank.banque || ''} onChange={(e) => setBank({ ...bank, banque: e.target.value })} /></div>
            <div><Label className="text-xs">Référence à indiquer (pattern, {'{nom}'} = nom bénéficiaire)</Label><Input value={bank.reference_pattern || ''} onChange={(e) => setBank({ ...bank, reference_pattern: e.target.value })} /></div>
            <div>
              <Label className="text-xs">RIB officiel (PDF, remplace l'actuel)</Label>
              <input type="file" accept="application/pdf" onChange={(e) => setRibFile(e.target.files?.[0] || null)} data-testid="settings-rib-upload" />
              {bank.rib_pdf_path && <p className="text-[10px] text-emerald-700 mt-1">✓ RIB actuel : {bank.rib_pdf_path}</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="settings-save">
            {saving && <Loader2 size={14} className="animate-spin mr-2" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditKitDialog({ kit, onClose, onSaved }) {
  const [form, setForm] = useState({
    kit_type: kit.kit_type,
    amount: kit.amount || '',
    beneficiary: {
      name: kit.beneficiary?.name || '',
      email: kit.beneficiary?.email || '',
      phone: kit.beneficiary?.phone || '',
      org_name: kit.beneficiary?.org_name || '',
    },
    admin_notes: kit.admin_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.beneficiary.name || !form.beneficiary.email) return toast.error('Nom et email requis');
    setSaving(true);
    try {
      await updateAdhesionKit(kit.id, {
        kit_type: form.kit_type,
        amount: form.amount ? Number(form.amount) : null,
        beneficiary: form.beneficiary,
        admin_notes: form.admin_notes,
      });
      toast.success('Kit mis à jour');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-kit-dialog">
        <DialogHeader><DialogTitle>Modifier le kit — {kit.beneficiary?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Type de kit</Label>
              <Select value={form.kit_type} onValueChange={(v) => setForm({ ...form, kit_type: v })}>
                <SelectTrigger data-testid="edit-kit-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {KIT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Montant (€)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="edit-kit-amount" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Nom du bénéficiaire *</Label>
            <Input value={form.beneficiary.name} onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, name: e.target.value } })} data-testid="edit-kit-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Email *</Label>
              <Input type="email" value={form.beneficiary.email} onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, email: e.target.value } })} data-testid="edit-kit-email" />
            </div>
            <div>
              <Label className="text-xs">Téléphone</Label>
              <Input value={form.beneficiary.phone} onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, phone: e.target.value } })} />
            </div>
          </div>
          {form.kit_type !== 'associe_pp' && (
            <div>
              <Label className="text-xs">Organisation</Label>
              <Input value={form.beneficiary.org_name} onChange={(e) => setForm({ ...form, beneficiary: { ...form.beneficiary, org_name: e.target.value } })} />
            </div>
          )}
          <div>
            <Label className="text-xs">Notes administratives (internes)</Label>
            <Textarea rows={3} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} data-testid="edit-kit-notes" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button className="bg-[#2D5A4C] hover:bg-[#1E4136]" disabled={saving} onClick={save} data-testid="edit-kit-save">
            {saving ? <Loader2 className="animate-spin mr-1" size={14}/> : null}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
